/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { getTenantLogoUrl } from "../../utils/getTenantLogoUrl";

export default function useCompanyInfo(isArabic, navigate) {
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 576);

  const [form, setForm] = useState({
    logo: null,
    storeName: "",
    storeSlug: "",
    sector: "",
    paymentMethods: [],
    services: "",
  });

  const [preview, setPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, "");

  /* ----------------------------------------------------
        (1) HANDLE SCREEN SIZE
  ---------------------------------------------------- */
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 576);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* ----------------------------------------------------
        (2) PREFILL COMPANY INFO FROM LOCALSTORAGE
        Supports:
        - Login response
        - Register response
        - Refresh
  ---------------------------------------------------- */
  useEffect(() => {
    try {
      let tenant = {};
      let tenantDetails = {};

      // Try Register first
      const reg = JSON.parse(localStorage.getItem("fullRegisterResponse"));

      if (reg?.tenant) {
        tenant = reg.tenant;
        tenantDetails = {}; // registration does NOT have tenantDetails yet
      } else {
        // Try Login
        const login = JSON.parse(localStorage.getItem("fullLoginResponse"));
        tenant = JSON.parse(localStorage.getItem("tenant")) || login?.tenant || {};
        tenantDetails = JSON.parse(localStorage.getItem("tenantDetails")) || login?.tenantDetails || {};
      }

      // Update form
      setForm({
        logo: null,
        storeName: tenantDetails.companyName || tenant.tenantName || "",
        storeSlug: tenantDetails.storeSlug || "",
        sector: tenantDetails.sectorType || "",
        paymentMethods: tenantDetails.paymentMethods || [],
        services: tenantDetails.businessName || "",
      });

      // Logo preview
      const logoUrl = getTenantLogoUrl(tenantDetails.logo || tenant.logo || tenant.Logo);
      if (logoUrl) {
        setPreview(logoUrl);
      }
    } catch (err) {
      console.error("Error loading company info:", err);
    }
  }, []);

  /* ----------------------------------------------------
        TOAST HELPERS
  ---------------------------------------------------- */
  const showError = (msg) =>
    toast.error(msg, { position: "top-center", rtl: isArabic });

  const showSuccess = (msg) =>
    toast.success(msg, { position: "top-center", rtl: isArabic });

  /* ----------------------------------------------------
        FORM HANDLERS
  ---------------------------------------------------- */
  const handleChange = (e) => {
    let { name, value } = e.target;

    if (name === "storeSlug") {
      value = value
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, "") // Allow only specific chars
        .replace(/--+/g, "-");      // Prevent multiple dashes
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoUpload = (file) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showError(isArabic ? "يرجى اختيار صورة فقط" : "Please select an image only");
      return;
    }

    setForm({ ...form, logo: file });
    setPreview(URL.createObjectURL(file));
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    handleLogoUpload(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    handleLogoUpload(file);
  };

  const handleRemoveImage = () => {
    setForm({ ...form, logo: null });
    setPreview(null);

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handlePaymentChange = (e) => {
    const { value, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      paymentMethods: checked
        ? [...prev.paymentMethods, value]
        : prev.paymentMethods.filter((m) => m !== value),
    }));
  };

  /* ----------------------------------------------------
        SUBMIT
  ---------------------------------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!preview && !form.logo)
      return showError(isArabic ? "الرجاء رفع شعار المتجر 📸" : "Please upload your store logo 📸");

    if (!form.storeName.trim())
      return showError(isArabic ? "الرجاء إدخال اسم المتجر 🏪" : "Please enter your store name 🏪");

    if (!form.storeSlug.trim())
      return showError(isArabic ? "الرجاء إدخال رابط المتجر (Slug) 🔗" : "Please enter your store slug 🔗");

    if (!form.sector.trim())
      return showError(isArabic ? "الرجاء إدخال نوع القطاع 🏷️" : "Please enter your business sector 🏷️");

    if (form.paymentMethods.length === 0)
      return showError(isArabic ? "اختر طريقة دفع واحدة على الأقل 💳" : "Select at least one payment method 💳");

    if (!form.services.trim())
      return showError(isArabic ? "أدخل الخدمات المقدمة ✍️" : "Please describe your services ✍️");

    setLoading(true);

    try {
      const tenant = JSON.parse(localStorage.getItem("tenant")) || {};
      const tenantDetails = JSON.parse(localStorage.getItem("tenantDetails")) || {};
      const currentTenantId = tenant.tenantId || tenantDetails.tenantId || 0;

      // Validate if slug already exists
      try {
        const validationRes = await fetch(`${baseUrl}/GetAllTenantFullDetails?tenantId=-1`);
        if (validationRes.ok) {
          const allTenants = await validationRes.json();
          const existing = allTenants.find(t =>
            t.TenantId !== currentTenantId &&
            ((t.storeSlug && t.storeSlug.toLowerCase() === form.storeSlug.trim().toLowerCase()) ||
              (t.StoreSlug && t.StoreSlug.toLowerCase() === form.storeSlug.trim().toLowerCase()))
          );
          if (existing) {
            setLoading(false);
            return showError(isArabic ? "اسم الرابط (Slug) مستخدم مسبقاً، يرجى اختيار اسم آخر ❌" : "This store slug is already used by another store, please choose another one ❌");
          }
        }
      } catch (err) {
        console.error("Failed to validate slug", err);
      }

      const fd = new FormData();

      // ✅ 🚦 تحسين عملية إرسال البيانات لضمان عدم ضيابها أو إرسال null
      // نأخذ البيانات من tenant أولاً (لأنها الأحدث بعد الـ Register) ثم tenantDetails
      const finalTenantId = tenant.tenantId || tenantDetails.tenantId || 0;
      const finalTenantName = tenant.tenantName || tenantDetails.tenantName || tenantDetails.companyName || "";
      const finalEngTenantName = tenant.engTenantName || tenantDetails.engTenantName || finalTenantName;
      const finalEmail = tenant.email || tenantDetails.email || "";
      const finalPhone = tenant.phone || tenantDetails.phone || "";
      const finalCreatedAt = tenant.createdAt || tenant.CreatedAt || tenantDetails.createdAt || new Date().toISOString();

      fd.append("TenantId", finalTenantId);
      fd.append("TenantName", finalTenantName);
      fd.append("EngTenantName", finalEngTenantName);
      fd.append("Email", finalEmail);
      fd.append("Phone", finalPhone);
      fd.append("SubDomain", tenant.subDomain || tenantDetails.subDomain || "");
      fd.append("IsActive", tenant.isActive !== undefined ? tenant.isActive : 1);
      fd.append("CreatedAt", finalCreatedAt);
      fd.append("Product", tenant.product || tenantDetails.product || "");
      fd.append("TenantDtlId", tenantDetails.tenantDtlId || 0);

      // Logo
      if (form.logo) fd.append("LogoFile", form.logo);

      // Updated company info
      fd.append("ClientName", finalTenantName);
      fd.append("SectorType", form.sector);
      fd.append("BusinessName", form.services);
      fd.append("CompanyName", form.storeName);
      fd.append("StoreSlug", form.storeSlug);

      // ✅ الحفاظ على حقول الخطة والاشتراك (حتى لو فارغة حالياً) لمنع مسح الحقول في الـ API
      fd.append("MainPlanId", tenantDetails.mainPlanId || "");
      fd.append("PlanType", tenantDetails.planType || "");
      fd.append("Price", tenantDetails.price || 0);
      fd.append("Descripsion", tenantDetails.descripsion || "");
      fd.append("EngPlanType", tenantDetails.engPlanType || "");
      fd.append("EngDescripsion", tenantDetails.engDescripsion || "");

      // Notes 1-8 (إرسالها فارغة إذا لم تكن موجودة لمنع الـ Null في القاعدة)
      for (let i = 1; i <= 8; i++) {
        fd.append(`Note${i}`, tenantDetails[`note${i}`] || "");
      }

      // Payment Methods
      fd.append("PaymentMethods", JSON.stringify(form.paymentMethods));

      const authToken = localStorage.getItem("authToken");

      const res = await fetch(`${baseUrl}/UpdateTenantDetails/UpdateTenantFullDetails`, {
        method: "POST",
        headers: { "Authorization": authToken ? `Bearer ${authToken}` : "" },
        body: fd,
      });

      if (!res.ok) throw new Error("Error updating company info");

      const resData = await res.json();

      // Save updated tenantDetails (Merge with existing to prevent data loss)
      const mergedDetails = { ...tenantDetails, ...resData };
      localStorage.setItem("tenantDetails", JSON.stringify(mergedDetails));
      // Save for ThemePlan validation
      localStorage.setItem("companyStoreName", form.storeName);
      localStorage.setItem("companyStoreSlug", form.storeSlug);
      localStorage.setItem("companySector", form.sector);
      localStorage.setItem("companyServices", form.services);
      localStorage.setItem("companyPaymentMethods", JSON.stringify(form.paymentMethods));

      // 2️⃣ حفظ طرق الدفع باستخدام الـ API المخصص (SavePaymentMethods) لضمان تسجيلها
      try {
        const gRes = await fetch(`${baseUrl}/PaymentMethods/GetPaymentMethods?TenantId=-1`, {
          headers: { "Authorization": authToken ? `Bearer ${authToken}` : "" }
        });
        if (gRes.ok) {
          const allGlobal = await gRes.json();
          const pMethodsPayload = (form.paymentMethods || []).map(selectedName => {
            const found = Array.isArray(allGlobal) && allGlobal.find(g =>
              (g.paymentMethodName || "").toLowerCase().includes(selectedName.toLowerCase()) ||
              (g.engPaymentMethodName || "").toLowerCase().includes(selectedName.toLowerCase())
            );
            return {
              tenantId: parseInt(finalTenantId),
              paymentMethodId: found ? (found.paymentMethodId || found.id) : 0,
              paymentMethodName: selectedName,
              engPaymentMethodName: selectedName,
              isActive: 1
            };
          });

          if (pMethodsPayload.length > 0) {
            await fetch(`${baseUrl}/PaymentMethods/SavePaymentMethods`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": authToken ? `Bearer ${authToken}` : ""
              },
              body: JSON.stringify(pMethodsPayload)
            });
          }
        }
      } catch (pmErr) {
        console.error("Payment save failed:", pmErr);
      }

      showSuccess(isArabic ? "تم حفظ البيانات بنجاح ✅" : "Data saved successfully ✅");

      setTimeout(() => navigate("/theme-plan"), 1200);

    } catch (err) {
      showError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    isMobile,
    form,
    preview,
    isDragging,
    fileInputRef,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileInputChange,
    handleRemoveImage,
    handleChange,
    handlePaymentChange,
    handleSubmit,
  };
}
